import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { FileText, Receipt, Eye, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Documents commerciaux de l'opération — Devis + Facture
 * Auto-générés à la volée via /api/documents/generate/{type}/{id}
 * Boutons Voir (ouvre PDF dans nouvel onglet) + Télécharger (force download)
 */
const OperationDocumentsSection = ({ operationId, operation = {} }) => {
  const [busy, setBusy] = useState({}); // { quote-view: true, invoice-download: false, ... }

  const setKey = (k, v) =>
    setBusy((prev) => ({ ...prev, [k]: v }));

  // Disponibilité des documents selon statut
  const status = operation.status;
  const finStatus = operation.financialStatus || operation.financial_status;

  const quoteAvailable = !['demande_enregistree', 'en_attente_de_devis', 'nouvelle'].includes(status);
  const invoiceAvailable =
    ['en_attente_paiement', 'payee', 'terminee', 'facturee'].includes(status) ||
    ['invoiced', 'pending_payment', 'partially_paid', 'paid'].includes(finStatus) ||
    !!operation.invoiceNumber;

  const fetchPdf = async (type) => {
    const res = await axios.post(
      `${API}/documents/generate/${type}/${operationId}`,
      {},
      { responseType: 'blob'},
    );
    return new Blob([res.data], { type: 'application/pdf' });
  };

  const handleView = async (type) => {
    const k = `${type}-view`;
    setKey(k, true);
    try {
      const blob = await fetchPdf(type);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // libérer plus tard
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("Document non disponible pour le moment");
    } finally {
      setKey(k, false);
    }
  };

  const handleDownload = async (type, label) => {
    const k = `${type}-dl`;
    setKey(k, true);
    try {
      const blob = await fetchPdf(type);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label}_${operation.operationNumber || operationId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${label} téléchargé`);
    } catch (e) {
      toast.error("Impossible de télécharger le document");
    } finally {
      setKey(k, false);
    }
  };

  const ROWS = [
    {
      type: 'quote',
      label: 'Devis',
      icon: FileText,
      iconColor: 'text-blue-600 bg-blue-50',
      available: quoteAvailable,
      number: operation.quoteNumber,
      missingHint: 'Le devis sera disponible dès qu\'il aura été émis',
    },
    {
      type: 'invoice',
      label: 'Facture',
      icon: Receipt,
      iconColor: 'text-amber-600 bg-amber-50',
      available: invoiceAvailable,
      number: operation.invoiceNumber,
      missingHint: 'La facture sera disponible une fois l\'opération facturée',
    },
  ];

  return (
    <Card data-testid="operation-commercial-documents">
      <CardHeader className="py-3 bg-gradient-to-r from-amber-50 to-orange-50">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="w-5 h-5 text-amber-600" />
          Documents de l'opération
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {ROWS.map((row) => {
            const Icon = row.icon;
            const viewBusy = busy[`${row.type}-view`];
            const dlBusy = busy[`${row.type}-dl`];
            return (
              <div
                key={row.type}
                className="flex items-center justify-between gap-3 px-4 py-3 flex-wrap"
                data-testid={`doc-row-${row.type}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center ${row.iconColor}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{row.label}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {row.available
                        ? row.number
                          ? `N° ${row.number}`
                          : 'Disponible — généré automatiquement'
                        : row.missingHint}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {row.available ? (
                    <>
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
                        Disponible
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleView(row.type)}
                        disabled={viewBusy}
                        data-testid={`view-${row.type}-btn`}
                      >
                        {viewBusy ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                        <span className="ml-1.5 hidden sm:inline">Voir</span>
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleDownload(row.type, row.label)}
                        disabled={dlBusy}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`download-${row.type}-btn`}
                      >
                        {dlBusy ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        <span className="ml-1.5 hidden sm:inline">Télécharger</span>
                      </Button>
                    </>
                  ) : (
                    <Badge variant="outline" className="text-gray-500 border-gray-300">
                      En attente
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default OperationDocumentsSection;
